import { ChevronLeft, Edit2, Loader2, Plus, Trash2, Users } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  MatterForm,
  MatterList,
  type Client,
  type CreateMatterInput,
  type Matter,
  type UpdateMatterInput,
} from "@lexiun/org-admin";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { usePlatformApi } from "../hooks/usePlatformApi";
import { PlatformApiError } from "../lib/platform-api";

/**
 * Matters admin page (Phase D/D3).
 *
 * Mounted at `/orgs/:orgId/clients/:clientId/matters`. Lists every
 * matter under one client via the Phase B lexserver routes
 * (`/api/org/:orgId/clients/:clientId/matters`) and renders the
 * shared `@lexiun/org-admin` `MatterList` + `MatterForm` primitives
 * against real data. Paired with `tests/e2e/matters.spec.ts`.
 */
export function Matters() {
  const { orgId = "", clientId = "" } = useParams<{
    orgId: string;
    clientId: string;
  }>();
  const api = usePlatformApi();

  const [client, setClient] = useState<Client | null>(null);
  const [matters, setMatters] = useState<Matter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<Matter | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!orgId || !clientId) return;
    try {
      setLoading(true);
      const [clientRes, mattersRes] = await Promise.all([
        api.getClient(orgId, clientId),
        api.listMatters(orgId, clientId),
      ]);
      setClient(clientRes.data);
      setMatters(mattersRes.data);
      setError(null);
    } catch (e) {
      setError(
        e instanceof PlatformApiError
          ? `${e.code}: ${e.message}`
          : e instanceof Error
            ? e.message
            : "Failed to load matters",
      );
    } finally {
      setLoading(false);
    }
  }, [api, orgId, clientId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCreate = async (input: CreateMatterInput | UpdateMatterInput) => {
    setSubmitting(true);
    setError(null);
    try {
      // Create-mode is the only path that reaches here; cast is safe.
      await api.createMatter(orgId, clientId, input as CreateMatterInput);
      setShowCreate(false);
      await load();
    } catch (e) {
      setError(
        e instanceof PlatformApiError
          ? `${e.code}: ${e.message}`
          : e instanceof Error
            ? e.message
            : "Failed to create matter",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (input: CreateMatterInput | UpdateMatterInput) => {
    if (!editing) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.updateMatter(
        orgId,
        clientId,
        editing.matterId,
        input as UpdateMatterInput,
      );
      setEditing(null);
      await load();
    } catch (e) {
      setError(
        e instanceof PlatformApiError
          ? `${e.code}: ${e.message}`
          : e instanceof Error
            ? e.message
            : "Failed to update matter",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (matter: Matter) => {
    const ok = window.confirm(
      `Delete matter "${matter.name}"? Any cases under it will be orphaned.`,
    );
    if (!ok) return;
    setDeleteLoading(matter.matterId);
    setError(null);
    try {
      await api.deleteMatter(orgId, clientId, matter.matterId);
      await load();
    } catch (e) {
      setError(
        e instanceof PlatformApiError
          ? `${e.code}: ${e.message}`
          : e instanceof Error
            ? e.message
            : "Failed to delete matter",
      );
    } finally {
      setDeleteLoading(null);
    }
  };

  // When the client fetch fails (e.g. deleted client, stale URL) we
  // render a dedicated "not found" state so the user isn't staring at
  // an empty matter list under a blank header.
  const clientMissing = !loading && !client;

  if (clientMissing) {
    return (
      <div className="space-y-4" data-testid="matters-page">
        <Link
          to={`/orgs/${orgId}/clients`}
          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
        >
          <ChevronLeft className="h-3 w-3" /> Back to clients
        </Link>
        <div
          role="alert"
          className="rounded border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300"
          data-testid="matters-client-not-found"
        >
          <p className="font-medium">Client not found</p>
          <p className="mt-1 text-xs">
            {error ?? `No client with id ${clientId} exists in org ${orgId}.`}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="matters-page">
      <header className="flex items-center justify-between">
        <div>
          <Link
            to={`/orgs/${orgId}/clients`}
            className="mb-2 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
          >
            <ChevronLeft className="h-3 w-3" /> Back to clients
          </Link>
          <h1 className="text-2xl font-semibold">
            {client ? client.name : "Matters"}
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {loading
              ? "Loading..."
              : `${matters.length} matter${matters.length === 1 ? "" : "s"} under ${client?.name ?? clientId}`}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          disabled={!client || loading}
          className="inline-flex items-center gap-2 rounded bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          data-testid="matters-create-button"
        >
          <Plus className="h-4 w-4" />
          New matter
        </button>
      </header>

      {error && (
        <div
          role="alert"
          className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300"
          data-testid="matters-error"
        >
          {error}
        </div>
      )}

      <div className="rounded border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
        {/*
          No `onSelect` in D3: the matter-detail page is owned by
          LexIDE (D8), not LexPortal. Row clicks are therefore inert
          here except via the edit/delete action buttons.
        */}
        <MatterList
          matters={matters}
          loading={loading}
          renderActions={(matter) => (
            <>
              <Link
                to={`/orgs/${orgId}/clients/${clientId}/matters/${matter.matterId}/assignments`}
                onClick={(e) => e.stopPropagation()}
                title="Manage matter assignments"
                className="rounded p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                data-testid={`matter-assignments-${matter.matterId}`}
              >
                <Users className="h-3.5 w-3.5" />
              </Link>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditing(matter);
                }}
                title="Edit matter"
                className="rounded p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                data-testid={`matter-edit-${matter.matterId}`}
              >
                <Edit2 className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                disabled={deleteLoading === matter.matterId}
                onClick={(e) => {
                  e.stopPropagation();
                  void handleDelete(matter);
                }}
                title="Delete matter"
                className="rounded p-1 text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                data-testid={`matter-delete-${matter.matterId}`}
              >
                {deleteLoading === matter.matterId ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Trash2 className="h-3.5 w-3.5" />
                )}
              </button>
            </>
          )}
        />
      </div>

      <Dialog open={showCreate} onOpenChange={(o) => !o && setShowCreate(false)}>
        <DialogContent data-testid="matters-create-dialog">
          <DialogHeader>
            <DialogTitle>New matter</DialogTitle>
            <DialogDescription>
              Create a long-running matter under {client?.name ?? "this client"}.
              The matter type determines the workflow shape and is immutable
              after creation.
            </DialogDescription>
          </DialogHeader>
          <MatterForm
            onSubmit={handleCreate}
            onCancel={() => setShowCreate(false)}
            submitting={submitting}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="matters-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit matter</DialogTitle>
            <DialogDescription>
              Update the matter's name, status, or primary lead. The matter
              type cannot be changed.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <MatterForm
              initial={editing}
              onSubmit={handleEdit}
              onCancel={() => setEditing(null)}
              submitting={submitting}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
